import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

/**
 * "Install StreamFlow" PWA install prompt.
 *
 * Listens for the `beforeinstallprompt` event (Chrome / Edge / Samsung
 * Internet on Android), defers it, and surfaces a banner the user can
 * tap to trigger the actual install. iOS Safari doesn't fire this
 * event — for iOS the user has to "Add to Home Screen" via the share
 * sheet manually; we render a hint banner there instead.
 *
 * Dismissals persist (localStorage) so we don't badger anyone.
 */

const DISMISSED_KEY = 'gastv.installPrompt.dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [iosHint, setIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(localStorage.getItem(DISMISSED_KEY) === 'true');

  useEffect(() => {
    if (dismissed) return;

    // Already installed (running as standalone)? Don't prompt.
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    if (isStandalone) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari heuristic: no beforeinstallprompt support, but
    // window.MSStream missing + iOS user-agent is reliable enough.
    const ua = navigator.userAgent || '';
    const isIos = /iphone|ipad|ipod/i.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
    if (isIos) {
      // Give the page a moment to settle; show hint after 2s.
      const t = setTimeout(() => setIosHint(true), 2000);
      return () => { window.removeEventListener('beforeinstallprompt', handler); clearTimeout(t); };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [dismissed]);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
    setDeferred(null);
    setIosHint(false);
  };

  if (dismissed) return null;
  if (!deferred && !iosHint) return null;

  const handleInstall = async () => {
    if (!deferred) return;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === 'accepted') {
      // Browser handles the install; banner can disappear.
      setDeferred(null);
    } else {
      dismiss();
    }
  };

  return (
    <div className="bg-slate-900 text-white rounded-lg px-4 py-3 mb-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Download className="w-5 h-5 text-blue-400 flex-shrink-0" />
        <div className="text-sm min-w-0">
          {deferred ? (
            <>
              <p className="font-medium">Install StreamFlow</p>
              <p className="text-slate-300 text-xs mt-0.5">
                Adds an icon to your home screen. Opens like a native app.
              </p>
            </>
          ) : (
            <>
              <p className="font-medium">Add to home screen</p>
              <p className="text-slate-300 text-xs mt-0.5">
                Tap the share icon in Safari, then "Add to Home Screen".
              </p>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {deferred && (
          <button
            onClick={handleInstall}
            className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-500"
          >
            Install
          </button>
        )}
        <button
          onClick={dismiss}
          className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
